import {
  QueryClient, useMutation, useQueries, useQuery, useQueryClient,
} from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { ResponseModel } from "services/ApiClient/responseModel";
import { Task, TaskId, TaskSummary } from "features/Resources/Task/models";
import { apiTaskById } from "helpers/url/api/v2";
import { isInPast } from "helpers/time";
import { NewHireJourneyId } from "models/newHire";
import { useCallback, useMemo } from "react";
import tasksByJourneyBatcher from "../tasksByJourneyBatcher";

export const taskKeys = {
  all: ["tasks"] as const,
  count: () => [...taskKeys.all, "count"] as const,
  lists: () => [...taskKeys.all, "list"] as const,
  list: (filters: string) => [...taskKeys.lists(), { filters }] as const,
  details: () => [...taskKeys.all, "detail"] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  byNewHireJourneys: () => [...taskKeys.all, "newHireJourneys"] as const,
  byNewHireJourney: (id: string) => [...taskKeys.byNewHireJourneys(), id] as const,
  reminders: () => [...taskKeys.all, "reminders"] as const,
  remindersByTaskId: (taskId: string) => [...taskKeys.reminders(), taskId] as const,
};

export function setNewTaskInQueryClient(queryClient: QueryClient, newTask: Task) {
  queryClient.setQueryData(taskKeys.detail(newTask.id), (oldData: Task | undefined) => {
    if (!oldData) {
      return newTask;
    }
    return {
      ...oldData,
      ...newTask,
    };
  });
}

export function setNewTaskInListQueryClient(
  queryClient: QueryClient,
  updatedTask: Task,
  queryString: string,
) {
  queryClient.setQueryData<Task[]>(taskKeys.list(queryString), (prev) => {
    if (!prev) return [updatedTask];

    return prev.map((task) => (task.id === updatedTask.id ? updatedTask : task));
  });
}

export function setNewTaskInNewHireTaskListQueryClient(
  queryClient: QueryClient,
  updatedTask: Task,
  newHireJourneyId: NewHireJourneyId,
) {
  queryClient.setQueryData<Task[]>(taskKeys.byNewHireJourney(newHireJourneyId), (prev) => {
    if (!prev) return [updatedTask];

    return prev.map((task) => (task.id === updatedTask.id ? updatedTask : task));
  });
}

const taskDetailQueryArgs = (id: string | undefined, options: { enabled?: boolean } = {}) => ({
  queryKey: taskKeys.detail(id as string),
  queryFn: async () => apiClient.get<Task | null>(apiTaskById(id)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && (options.enabled ?? true),
});

export const useTaskDetailQuery = (id: string | undefined, options: { enabled?: boolean } = {}) => useQuery<Task | null>({
  ...taskDetailQueryArgs(id, options),
});

export function useTasksCountQuery(newHireJourneyId?: string | number | null) {
  const url = newHireJourneyId
    ? `${API_ROUTES.tasks.count}?${new URLSearchParams({ new_hire_journey_id: String(newHireJourneyId) })}`
    : API_ROUTES.tasks.count;
  return useQuery<{ count: number }>({
    queryKey: [...taskKeys.count(), newHireJourneyId ?? null],
    queryFn: async () => apiClient.get(url),
    staleTime: 1000 * 60 * 3,
  });
}

export function useTasksQueryByIds(taskIds: TaskId[], options: { enabled?: boolean } = {}) {
  const queries = useQueries({
    queries: taskIds.map((taskId) => ({
      ...taskDetailQueryArgs(taskId, options),
    })),
  });

  return queries.map((query, index) => ({
    taskId: taskIds[index],
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }));
}

export const useTaskListQuery = (queryString: string, options: { enabled?: boolean } = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<Task[]>({
    queryKey: queryString ? taskKeys.list(queryString) : taskKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.tasks.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

// Returns the full `{ data, meta }` envelope so callers can read pagination
// totals for server-paginated tables. Prefer `useTaskListQuery` when meta
// isn't needed.
export const useTaskListResponseQuery = (queryString: string, options: { enabled?: boolean } = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<ResponseModel<Task[]>>({
    queryKey: queryString ? [...taskKeys.list(queryString), "with-meta"] : [...taskKeys.lists(), "with-meta"],
    queryFn: async ({ signal }) =>
      apiClient
        .performRequest<ResponseModel<Task[]>>(`${generatePath(API_ROUTES.tasks.base)}${query}`, undefined, signal)
        .then((r) => r.payload),
    staleTime: 1000 * 60 * 3,
    ...options,
  });
};

export const useNewHireJourneyTasksQuery = (newHireJourneyId: NewHireJourneyId, options: { enabled?: boolean; [key: string]: any } = {}) => useQuery<Task[]>({
  queryKey: taskKeys.byNewHireJourney(newHireJourneyId),
  queryFn: async () => tasksByJourneyBatcher.fetch({ newHireJourneyId }),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!newHireJourneyId && (options.enabled ?? true),
});

function useTaskRequest(mutationFn: (data: Partial<Task>) => Promise<any>, onSuccess?: (res: any) => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      setNewTaskInQueryClient(queryClient, res);
      // Call custom onSuccess first (e.g., close modal) before cache invalidations
      onSuccess?.(res);
      // Invalidate all task-related caches to ensure UI stays in sync
      queryClient.invalidateQueries({ queryKey: taskKeys.byNewHireJourneys() });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.count() });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateTask(taskId: string | undefined) {
  return useTaskRequest(async (data: Partial<Task>) => apiClient.put<Task>(apiTaskById(taskId), { task: data }));
}

export function useCreateTask() {
  return useTaskRequest(async (data: Partial<Task>) => apiClient.post<Task>(API_ROUTES.tasks.base, { task: data }));
}

export function useSkipTask(task: Task, onSuccess?: (res: any) => void) {
  const toast = useToast();
  const effectiveStartDate = task?.startedAt || task?.startAt;
  const taskIncompleteStatus = effectiveStartDate && isInPast(effectiveStartDate) ? "active" : "upcoming";
  const { mutate } = useUpdateTask(task?.id);
  return () => mutate(
    { status: task?.status === "skipped" ? taskIncompleteStatus : "skipped" },
    { onSuccess: (res: Task) => {
      toast({ title: `Task ${res.status === "skipped" ? "skipped" : "re-opened"}` });
      onSuccess?.(res);
    } },
  );
}

export function useMarkCompleteTask(task: Task | TaskSummary | undefined | null, onSuccess?: (res: any) => void) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const effectiveStartDate = task?.startedAt || task?.startAt;
  const taskIncompleteStatus = effectiveStartDate && isInPast(effectiveStartDate) ? "active" : "upcoming";
  const { mutate } = useUpdateTask(task?.id);
  return () => mutate(
    { status: task?.status === "completed" ? taskIncompleteStatus : "completed" },
    { onSuccess: (res: Task) => {
      toast({ title: `Task ${res.status === "completed" ? "completed" : "re-opened"}` });
      queryClient.invalidateQueries({ queryKey: taskKeys.byNewHireJourneys() });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.count() });
      queryClient.invalidateQueries({ queryKey: taskKeys.details() });
      onSuccess?.(res);
    } },
  );
}

export function useMarkCompleteIfOpen(task: Task | TaskSummary | undefined | null, onSuccess?: (res: any) => void) {
  const markComplete = useMarkCompleteTask(task, onSuccess);
  return useCallback(() => {
    if (task) {
      // Always attempt to mark complete if task exists - the mutation handles idempotency
      markComplete();
    } else if (onSuccess) {
      // No task, still call onSuccess to continue the callback chain
      onSuccess(null);
    }
  }, [markComplete, task, onSuccess]);
}

export const useUrgentTasks = (tasks: Task[] | undefined = []) => useMemo(() => {
  const urgentTasks = tasks?.filter((task) => task.isUrgent || task.status === "failed") || [];
  return {
    hasUrgentTasks: urgentTasks.length > 0,
    urgentTasks,
    urgentCount: urgentTasks.length,
  };
}, [tasks]);
